import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './sidenav-item.scss?inline'

export type Spacing = 'top' | 'bottom' | 'both' | 'none'

/**
 * `dap-ds-sidenav-item`
 * @summary Side navigation item
 * @element dap-ds-sidenav-item
 * @title - Side navigation item
 * @group side-navigation
 *
 * @slot - The content of the side navigation item.
 * @slot suffix - The suffix of the side navigation item.
 *
 * @csspart base - The main side navigation item container.
 * @csspart link - The link of the side navigation item.
 *
 * @cssproperty --dds-sidenav-item-min-height - Minimum height of the sidenav item.
 * @cssproperty --dds-sidenav-item-margin-bottom - Default bottom margin of the sidenav item.
 * @cssproperty --dds-sidenav-item-padding - Padding of the sidenav item.
 * @cssproperty --dds-sidenav-item-radius - Border radius of the sidenav item.
 * @cssproperty --dds-sidenav-item-color - Text color of the sidenav item.
 * @cssproperty --dds-sidenav-item-font-size - Font size of the sidenav item.
 * @cssproperty --dds-sidenav-item-font-weight - Font weight of the sidenav item.
 * @cssproperty --dds-sidenav-item-spacing-margin - Margin used for spacing variants.
 * @cssproperty --dds-sidenav-item-bg-active - Background color when the item is active.
 * @cssproperty --dds-sidenav-item-bg-hover - Background color on hover and active states.
 */
@customElement('dap-ds-sidenav-item')
export default class DapDSSideNavItem extends DdsElement {
  /** The href of the side navigation item */
  @property() href: string

  /** Whether the side navigation item is active */
  @property({ type: Boolean, reflect: true }) active = false

  /** The spacing of the side navigation item
   * @type {'top' | 'bottom' | 'both' | 'none'}
   */
  @property({ reflect: true }) spacing: Spacing = 'none'

  static readonly styles = unsafeCSS(styles)

  render() {
    return html`
      <li
        part="base"
        role="listitem"
        class=${clsx('sidenav-item', `sidenav-item--spacing-${this.spacing}`, {
          'sidenav-item--active': this.active,
        })}>
        <a
          part="link"
          href=${this.href}
          class="sidenav-item__link"
          target=${ifDefined(this.href ? '_self' : undefined)}
          rel=${ifDefined(this.href ? 'noreferrer noopener' : undefined)}>
          <slot></slot>
          <slot name="suffix"></slot>
        </a>
      </li>
    `
  }
}
